import { styled, alpha } from '@mui/material/styles';
import { Box } from '@mui/material';

// Backdrop
export const Backdrop = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.72)' : 'rgba(75, 85, 99, 0.5)',
  zIndex: 40,
  transition: 'opacity 0.3s',
}));

// Slide-out Panel
export const SlidePanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  backgroundColor: theme.palette.background.default,
  width: '80%',
  maxWidth: '1280px',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.45), 0 10px 10px -5px rgba(0, 0, 0, 0.35)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  zIndex: 50,
  transform: 'translateX(0)',
  transition: 'transform 0.3s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
}));

// Header
export const Header = styled(Box)({
  background: 'linear-gradient(to right, #4f46e5, #a21caf)',
  color: '#ffffff',
  padding: '16px 24px',
});

export const HeaderContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const HeaderTitle = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 600,
  margin: 0,
});

export const CloseButton = styled('button')({
  color: '#ffffff',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': {
    color: '#e5e7eb',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
});

// Progress Section
export const ProgressSection = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const ProgressHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
});

export const ProgressTitle = styled('h3')(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  margin: 0,
}));

export const ProgressLabel = styled('span')(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const ProgressBarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.12)
      : theme.palette.grey[200],
  borderRadius: '9999px',
  height: '8px',
}));

export const ProgressBar = styled(Box)<{ progress: number }>(({ progress }) => ({
  background: 'linear-gradient(to right, #a78bfa, #7c3aed)',
  height: '8px',
  borderRadius: '9999px',
  transition: 'width 0.5s',
  width: `${progress}%`,
}));

// Scrollable body that holds the active step. It fills the space left by the
// header + progress bar (flex:1 + minHeight:0) and scrolls as a whole when a
// step's content exceeds the available height, so no functional element is ever
// clipped at any zoom level. Steps that manage their own inner scroll (1/2/3)
// fill this exactly and don't trigger it; steps that grow (customer step) scroll here.
export const StepBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
});

// Success Screen
export const SuccessContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
});

export const SuccessContent = styled(Box)({
  textAlign: 'center',
  maxWidth: '448px',
});

export const SuccessIcon = styled(Box)({
  width: '80px',
  height: '80px',
  background: 'linear-gradient(to right, #0CBBDD, #10B981)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
});

export const SuccessTitle = styled('h3')(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '8px',
}));

export const SuccessMessage = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: '24px',
}));

export const SuccessOrderNumber = styled('span')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#7c3aed',
}));

export const SummaryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  padding: '16px',
  marginBottom: '24px',
}));

export const SummaryGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
  fontSize: '0.875rem',
});

export const SummaryLabel = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const SummaryValue = styled('div')(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#7c3aed',
}));

export const SummaryStatus = styled('div')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.success.light,
}));

// Action Buttons
export const ActionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const PrimaryButton = styled('button')({
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  background: 'linear-gradient(to right, #0CBBDD, #10B981)',
  color: '#ffffff',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    background: 'linear-gradient(to right, #0AA6C7, #0F9A6F)',
  },
});

export const SecondaryButton = styled('button')(({ theme }) => ({
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Print Modal
export const PrintModalBackdrop = styled(Box)({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
});

export const PrintModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  maxWidth: '448px',
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
}));

export const PrintModalHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const PrintModalTitle = styled('h3')(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  margin: 0,
}));

export const PrintModalBody = styled(Box)({
  padding: '24px',
});

export const PrintModalFooter = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  gap: '12px',
}));

// Receipt
export const Receipt = styled(Box)(({ theme }) => ({
  maxWidth: '384px',
  margin: '0 auto',
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

export const ReceiptHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '16px',
});

export const ReceiptTitle = styled('div')(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 700,
  marginBottom: '8px',
  color: theme.palette.text.primary,
}));

export const ReceiptInfo = styled('div')(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const ReceiptDivider = styled(Box)(({ theme }) => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  margin: '10px 0',
  paddingTop: '10px',
}));

export const ReceiptSection = styled(Box)({
  marginBottom: '16px',
});

export const ReceiptRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

export const ReceiptProductRow = styled(Box)({
  marginBottom: '12px',
});

export const ReceiptBold = styled('span')({
  fontWeight: 700,
});

export const ReceiptTotal = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.125rem',
});
